import React, {useCallback, useEffect, useState} from "react";
import {useNavigate} from "react-router-dom";
import {
  FaArrowRight,
  FaApple,
  FaGooglePlay,
  FaGraduationCap,
  FaBookOpen,
} from "react-icons/fa";
import DailyWords from "./DailyWords/DailyWords";
import HeroMarketingIntro from "./HeroMarketingIntro";
import {BilingualText} from "../../i18n/BilingualText";
import "./MainScreenStyles.css";
import brandenburgGate from "../../assets/mobile/daily_brandenburg_gate.jpg";
import cologneCathedral from "../../assets/mobile/daily_cologne_cathedral.jpg";
import hamburgElbphilharmonie from "../../assets/mobile/daily_hamburg_elbphilharmonie.jpg";
import neuschwansteinCastle from "../../assets/mobile/daily_neuschwanstein_castle.jpg";
import reichstagBuilding from "../../assets/mobile/daily_reichstag_building.jpg";
import iosAppQr from "../../assets/mobile/app_store_qr.png";
import androidAppQr from "../../assets/mobile/google_play_qr.png";

const IOS_STORE_URL =
  "https://apps.apple.com/de/app/learn-german-a1-c2/id6746383285?l=en-GB";
const ANDROID_STORE_URL =
  "https://play.google.com/store/apps/details?id=com.kugathaj.deutsch";

const heroSlides = [
  { image: brandenburgGate, title: "Brandenburger Tor", location: "Berlin" },
  { image: cologneCathedral, title: "Kölner Dom", location: "Köln" },
  { image: hamburgElbphilharmonie, title: "Elbphilharmonie", location: "Hamburg" },
  { image: neuschwansteinCastle, title: "Schloss Neuschwanstein", location: "Bayern" },
  { image: reichstagBuilding, title: "Reichstagsgebäude", location: "Berlin" },
];

/** Homepage only: keep the established website course, book, login and payment routes. */
const MainScreen: React.FC = () => {
  const navigate = useNavigate();
  const [activeHeroSlide, setActiveHeroSlide] = useState(0);
  const [heroPaused, setHeroPaused] = useState(false);
  const openRoute = useCallback((route: "/courses" | "/books") => {
    navigate(route);
  }, [navigate]);

  useEffect(() => {
    // Keep the landmark motion optional and pause when a visitor interacts.
    // A hidden tab should not keep cycling the showcase.
    const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)");
    if (reducedMotion.matches || heroPaused) return;

    const timer = window.setInterval(() => {
      if (document.visibilityState === "visible") {
        setActiveHeroSlide(previous => (previous + 1) % heroSlides.length);
      }
    }, 7000);

    return () => window.clearInterval(timer);
  }, [heroPaused]);

  return (
    <main className="main-home-container dlx-site-home" aria-label="DEAR LANGUAGE Startseite">
      <div className="dlx-content-width">
        <section className="dlx-intro dlx-intro-swapped" aria-labelledby="dlx-main-heading">
          <aside className="dlx-intro-aside dlx-hero-qr-aside dlx-hero-left-panel" aria-label="Wort des Tages und App-Downloads">
            <div className="dlx-intro-hero-daily dlx-left-daily" aria-label="Wort des Tages">
              <div className="dlx-intro-daily-sparkles" aria-hidden="true">
                <span /><span /><span /><span /><span /><span />
              </div>
              <DailyWords compact />
            </div>
            <div className="dlx-qr-intro">
              <BilingualText as="h2" className="dlx-aside-heading" de="Deine Lernbegleitung für unterwegs" />
            </div>
            <div className="dlx-qr-platforms">
              <a className="dlx-qr-platform dlx-qr-ios" href={IOS_STORE_URL} target="_blank" rel="noopener noreferrer" aria-label="App Store öffnen">
                <span className="dlx-qr-platform-title"><FaApple aria-hidden="true" /> iOS</span>
                <span className="dlx-qr-square"><img src={iosAppQr} alt="iOS App Store QR code" loading="eager" /></span>
                <span className="dlx-qr-platform-cta"><BilingualText de="App Store öffnen" compact /><FaArrowRight aria-hidden="true" /></span>
              </a>
              <a className="dlx-qr-platform dlx-qr-android" href={ANDROID_STORE_URL} target="_blank" rel="noopener noreferrer" aria-label="Google Play öffnen">
                <span className="dlx-qr-platform-title"><FaGooglePlay aria-hidden="true" /> Android</span>
                <span className="dlx-qr-square"><img src={androidAppQr} alt="Android Google Play QR code" loading="eager" /></span>
                <span className="dlx-qr-platform-cta"><BilingualText de="Google Play öffnen" compact /><FaArrowRight aria-hidden="true" /></span>
              </a>
            </div>
          </aside>
          <div className="dlx-intro-primary">
            <div
              className={`dlx-intro-screensaver ${heroPaused ? "is-paused" : ""}`}
              role="region"
              aria-roledescription="carousel"
              aria-label="Bilderserie mit berühmten Orten in Deutschland"
              onMouseEnter={() => setHeroPaused(true)}
              onMouseLeave={() => setHeroPaused(false)}
              onFocusCapture={() => setHeroPaused(true)}
              onBlurCapture={event => {
                if (!event.currentTarget.contains(event.relatedTarget as Node | null)) {
                  setHeroPaused(false);
                }
              }}
            >
              {heroSlides.map((slide, index) => {
                const isActive = index === activeHeroSlide;
                return (
                  <figure
                    key={slide.title}
                    className={`dlx-intro-slide ${isActive ? "is-active" : ""}`}
                    aria-hidden={isActive ? "false" : "true"}
                  >
                    <img src={slide.image} alt={`${slide.title}, ${slide.location}`} loading={index === 0 ? "eager" : "lazy"} />
                    <span className="dlx-intro-place-badge">{slide.title} · {slide.location}</span>
                  </figure>
                );
              })}
              <div className="dlx-hero-story">
                <HeroMarketingIntro />
                <div className="dlx-intro-hero-copy dlx-intro-editorial dlx-hero-marketing-actions" aria-label="Kurse und Bücher entdecken">
                  <div className="dlx-actions">
                    <button className="dlx-primary-action" type="button" onClick={() => openRoute("/courses")}>
                      <BilingualText de="Kursangebot entdecken" compact /> <FaArrowRight aria-hidden="true" />
                    </button>
                    <button className="dlx-secondary-action" type="button" onClick={() => openRoute("/books")}>
                      <BilingualText de="Bücherbibliothek öffnen" compact /> <FaArrowRight aria-hidden="true" />
                    </button>
                  </div>
                </div>
              </div>
              <div className="dlx-intro-glass-layer" aria-hidden="true">
                <span className="dlx-intro-glass-orb dlx-intro-glass-orb-1" />
                <span className="dlx-intro-glass-orb dlx-intro-glass-orb-2" />
                <span className="dlx-intro-glass-orb dlx-intro-glass-orb-3" />
              </div>
              <div className="dlx-intro-particles" aria-hidden="true">
                <span />
                <span />
                <span />
                <span />
                <span />
                <span />
                <span />
                <span />
              </div>
              <div className="dlx-intro-slide-dots" aria-label="Bildauswahl">
                {heroSlides.map((slide, index) => (
                  <button
                    key={slide.title}
                    type="button"
                    className={`dlx-intro-slide-dot ${index === activeHeroSlide ? "is-active" : ""}`}
                    aria-label={`${slide.title} anzeigen`}
                    aria-pressed={index === activeHeroSlide}
                    onClick={() => setActiveHeroSlide(index)}
                  />
                ))}
              </div>
            </div>
          </div>

        </section>

        {/* German-first, bilingual destinations; no repeated level labels or summaries. */}
        <section className="dlx-home-paths" aria-label="Lernangebote">
          <button
            type="button"
            className="dlx-home-path-card dlx-home-path-courses"
            onClick={() => openRoute("/courses")}
            aria-label="Kursangebot entdecken"
          >
            <span className="dlx-home-path-watermark" aria-hidden="true"><FaGraduationCap /></span>
            <span className="dlx-home-path-head">
              <span className="dlx-home-path-icon" aria-hidden="true"><FaGraduationCap /></span>
            </span>
            <BilingualText className="dlx-home-path-title" de="Deutschkurse" />
            <BilingualText
              className="dlx-home-path-description"
              de="Kursinformationen, Lernstufen, Dauer und Unterrichtsumfang auf einen Blick."
            />
            <span className="dlx-home-path-bottom">
              <span className="dlx-home-path-cta">
                <BilingualText de="Kursangebot entdecken" compact />
                <FaArrowRight aria-hidden="true" />
              </span>
            </span>
          </button>

          <button
            type="button"
            className="dlx-home-path-card dlx-home-path-books"
            onClick={() => openRoute("/books")}
            aria-label="Bücherbibliothek öffnen"
          >
            <span className="dlx-home-path-watermark" aria-hidden="true"><FaBookOpen /></span>
            <span className="dlx-home-path-head">
              <span className="dlx-home-path-icon" aria-hidden="true"><FaBookOpen /></span>
            </span>
            <BilingualText className="dlx-home-path-title" de="Deutschbücher" />
            <BilingualText
              className="dlx-home-path-description"
              de="60 Bücher, geordnet nach den sechs Kompetenzstufen A1 bis C2."
            />
            <span className="dlx-home-path-bottom">
              <span className="dlx-home-path-cta">
                <BilingualText de="Bücherbibliothek öffnen" compact />
                <FaArrowRight aria-hidden="true" />
              </span>
            </span>
          </button>
        </section>

      </div>
    </main>
  );
};

export default MainScreen;
